import clsx from 'clsx';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import AspectRatioContainer from 'components/about-us/AspectRatioContainer';
import Button from 'components/common/Button';
import { FadeIn } from 'components/common/FadeIn';
import ToggleButtonRow from 'components/common/ToggleButtonRow/ToggleButtonRow';
import { EnterButton } from 'components/home-page/enter-button/EnterButton';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import Image from 'next/image';
import Link from 'next/link';
import { HomePagePrize } from 'pages/home-page/HomePageV1';
import { useMemo, useState } from 'react';
import { capitalizeFirstLetter } from 'utils/common';
import { PRIZE_NUMBERS } from 'utils/constants';
import {
  homePagePrizeComponentCategories,
  HomePagePrizeComponentCategories,
} from './prize-slugs';
import styles from './Prizes.module.css';

/** We want specific prizes to appear in this order, which is different from the order of the ToggleButtonRow
 * categories. This is because of how good the prize's image aspect ratio in the design */
const preferredCategoryDisplayOrderForPrizeCards = [
  'Trips',
  'Lifestyle',
  'Experiences',
  'Tech',
  'Family',
];

interface Props {
  /** All of the prizes that could appear in the prize component, e.g. 5 for each category - NOTE - this should not be
   * optional nor undefined, however, it fails at build time, probably because the github action can't access the API */
  prizes: HomePagePrize[] | undefined;
}

const Prizes = observer((props: Props) => {
  const [selectedCategory, setSelectedCategory] =
    useState<HomePagePrizeComponentCategories>();
  const prizesToShow = useMemo(() => {
    if (selectedCategory) {
      return (
        props.prizes
          ?.filter(
            (x) =>
              x.mainCategory.toLowerCase() === selectedCategory?.toLowerCase(),
          )
          .slice(0, 5) ?? []
      );
    }

    // Grab the first item from every category
    return preferredCategoryDisplayOrderForPrizeCards
      .map((c) =>
        props.prizes?.find(
          (x) => x.mainCategory.toLowerCase() === c?.toLowerCase(),
        ),
      )
      .filter((x) => x)
      .slice(
        0,
        5,
      ) /* We need a type cast, as filter will not remove undefined from the type */ as typeof props.prizes;
  }, [selectedCategory]);

  const userStore = useUserStore();

  const showEnterButton = !userStore.subscriptionStatusInfo.subscriberStatus;

  return (
    <AboutUsModule background="white" verticalPadding="more" withMorePadding>
      <AboutUsSiteContainer styles2026>
        <FadeIn onScroll className={styles.introHeading}>
          <div className={styles.headingContainer}>
            <div className={styles.headingText}>
              <p className="daymadeTypographyTaglineAccent">
                1 entry, {PRIZE_NUMBERS.PREMIUM} prizes
              </p>
              <h2 className={clsx('daymadeTypographyXL', styles.title)}>
                What would you choose?
              </h2>
            </div>
            {showEnterButton && (
              <span className="desktopOnly">
                <EnterButton
                  overrideButtonText="Enter to Win"
                  overrideReferralText
                  dm2026Button
                  buttonSize="Large"
                />
              </span>
            )}
          </div>
        </FadeIn>
        <FadeIn onScroll delay={0.1} className={clsx(styles.toggleButtonRow)}>
          <ToggleButtonRow
            items={homePagePrizeComponentCategories}
            initialSelectedItem={selectedCategory}
            onChange={(x) => setSelectedCategory(x)}
            allowClickToClearSelection
          />
        </FadeIn>
        <div>
          <div className={styles.prizeContainer} key={selectedCategory}>
            <AspectRatioContainer useCssVariableSizes>
              {prizesToShow?.map((x, i) => (
                <FadeIn
                  onScroll
                  delay={0.05 * (1 + i)}
                  className={styles.prizeCard}
                  key={i}
                >
                  <Image
                    className={styles.prizeCardImage}
                    src={x.imageSrc}
                    alt={x.imageAlt}
                    width={5000}
                    height={5000}
                    sizes="auto"
                  />
                  <Link
                    className={clsx(
                      styles.prizeCardText,
                      'daymadeTypographySMedium',
                    )}
                    href={x.href}
                  >
                    {x.title}
                  </Link>
                  {!selectedCategory && (
                    <div className={styles.categoryBadge}>
                      {selectedCategory ||
                        capitalizeFirstLetter(x.mainCategory)}
                    </div>
                  )}
                </FadeIn>
              ))}
              <FadeIn
                onScroll
                delay={0.3}
                className={styles.viewAllPrizesContainer}
              >
                <Link className={styles.viewAllPrizesCard} href="/prizes">
                  <span className="daymadeTypographyXL">
                    +{PRIZE_NUMBERS.PREMIUM.replace('+', '')}
                  </span>
                  <span
                    className={clsx(
                      'daymadeTypographyMRegular',
                      styles.viewAllPrizesMoreText,
                    )}
                  >
                    more prizes
                  </span>
                  <Button buttonStyle="Secondary" buttonSize="Large">
                    View All Prizes
                  </Button>
                </Link>
              </FadeIn>
            </AspectRatioContainer>
          </div>
          <FadeIn onScroll className={styles.buttonContainer}>
            {showEnterButton && (
              <EnterButton
                overrideButtonText="Enter to Win"
                overrideReferralText
                dm2026Button
              />
            )}
            <Button fullWidth="flex1" buttonStyle="Secondary" href="/prizes">
              View All Prizes
            </Button>
          </FadeIn>
        </div>
      </AboutUsSiteContainer>
    </AboutUsModule>
  );
});

export default Prizes;
